import { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Qué se configura en una sección, **en cuatro respuestas**.
 *
 * **Por qué existe una forma única y no una explicación por pantalla.** Diego lo dijo el
 * 2026-08-26: *"se agregaron muchísimas parametrizaciones y no tengo idea cómo afectan a los
 * agentes, a la cápsula, al tenant, a las tareas, a los tickets, a los avisos"*. Doce primitivas
 * nuevas en pocas sesiones, cada una con su configuración, y la ayuda no creció con ellas.
 *
 * El plan repitió muchas veces que *una primitiva sin pantalla es correcta y muerta*. El corolario
 * que faltaba: **una pantalla sin explicación se usa a ciegas**, y configurar sin saber el efecto
 * es más peligroso que no configurar.
 *
 * **Las cuatro respuestas, en este orden**, porque es el orden en que alguien las necesita:
 *
 * 1. `queEs` — en una línea, sin el nombre técnico;
 * 2. `paraQue` — qué problema resuelve;
 * 3. `aQueAfecta` — a los agentes, a los casos, a los avisos, a quién;
 * 4. `ejemplo` — configurado así, pasa esto.
 *
 * **Arranca cerrada.** Quien ya sabe no tiene que esquivarla cada vez que entra; quien no, la
 * abre. Es la doctrina de la pantalla: la ayuda va al costado y no en un modal.
 */
export default function QueSeConfiguraAca({ queEs, paraQue, aQueAfecta, ejemplo, ojo, testId }) {
  const [abierta, setAbierta] = useState(false);

  return (
    <div className="mb-2" data-testid={testId || 'que-se-configura-aca'}>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="flex items-center gap-1 text-[10px] text-brand-primary hover:text-[#001F6D]"
      >
        <HelpCircle className="w-3 h-3" />
        <span className="font-medium">{abierta ? 'Ocultar' : '¿Qué se configura acá?'}</span>
        {abierta ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>

      {abierta && (
        <div className="mt-1.5 text-[11px] text-zinc-700 bg-zinc-50 border border-brand-border rounded-sm p-3 space-y-2 max-w-3xl">
          <p>{queEs}</p>
          {paraQue && (
            <p><span className="font-semibold text-zinc-800">Para qué sirve. </span>{paraQue}</p>
          )}
          {aQueAfecta && (
            <p><span className="font-semibold text-zinc-800">A qué afecta. </span>{aQueAfecta}</p>
          )}
          {ejemplo && (
            <p className="text-zinc-600"><span className="font-semibold text-zinc-800">Ejemplo. </span>{ejemplo}</p>
          )}
          {/* Lo que conviene saber **antes** de tocar, no después. Va último y se ve distinto:
              es lo único que puede hacer daño. */}
          {ojo && (
            <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded-sm p-2">
              <span className="font-semibold">Ojo. </span>{ojo}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
